import { isEqual } from 'lodash-es';
import { useContext } from 'react';

import { useStoreSelector } from '../../../core/store/selector.js';
import { XYChartStoreContext } from '../context/XYChartStore.context.js';
import type { XYChartState } from '../types/state.js';

export const useSelector = <T>(
  selector: (state: XYChartState) => T,
  compareFn?: (a: T, b: T) => boolean,
): T => useStoreSelector(XYChartStoreContext, selector, compareFn);

export const useSetState = () => {
  const { setState } = useContext(XYChartStoreContext);

  return setState;
};

export const useXYChartHighlightedSeriesIdsState = () =>
  useSelector((state: XYChartState) => state.highlightedSeriesId, isEqual);

export const useInteractionModeState = () =>
  useSelector((state) => state.interactionMode);

export const useZoomState = () => useSelector((state) => state.zoom, isEqual);

export const useCurrentDomainState = () =>
  useSelector((state) => state.currentDomain, isEqual);

export const useSelectState = () => useSelector((state) => state.select);

export const useHoveredThresholdState = () =>
  useSelector((state) => state.thresholds.hoveredThresholdId);

export const useAnnotationsState = () =>
  useSelector((state) => state.annotations);

const areSetsEqual = (
  a: Set<string> | undefined,
  b: Set<string> | undefined,
): boolean => {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (a.size !== b.size) {
    return false;
  }
  for (const value of a) {
    if (!b.has(value)) {
      return false;
    }
  }
  return true;
};

export const useFilteredSeriesIdsState = () =>
  useSelector((state) => state.filteredSeriesIds, areSetsEqual);
